/**
 * ThemeProvider
 * Manages dark/light theme DOM effects using Zustand store
 * Wraps Ant Design ConfigProvider with shared theme
 */

import React, { useEffect } from "react";

import { ConfigProvider, theme } from "antd";

import { useThemeStore } from "@/store/themeStore";

const { darkAlgorithm } = theme;

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: "light" | "dark" | "system";
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "dark",
}) => {
  const currentTheme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setIsDark = useThemeStore((s) => s.setIsDark);

  // Initialize theme on mount if not set
  useEffect(() => {
    const storedTheme = localStorage.getItem("falcon-terminal-theme");
    if (!storedTheme) {
      setTheme(defaultTheme);
    }
  }, [defaultTheme, setTheme]);

  // Apply theme to DOM
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    let effectiveTheme = currentTheme;
    if (currentTheme === "system") {
      effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }

    root.classList.add(effectiveTheme);
    setIsDark(effectiveTheme === "dark");
  }, [currentTheme, setIsDark]);

  // Listen for system theme changes
  useEffect(() => {
    if (currentTheme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(e.matches ? "dark" : "light");
      setIsDark(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [currentTheme, setIsDark]);

  const antdTheme = {
    algorithm: darkAlgorithm,
    token: {
      colorPrimary: "#BB8800",
      colorSuccess: "#00b42a",
      colorWarning: "#ff7d00",
      colorError: "#f53f3f",
      colorInfo: "#165dff",
      colorTextBase: "#e0e0e0",
      colorBgBase: "#1a1a1a",
    },
  };

  return <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>;
};

export default ThemeProvider;
